import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild,
  OnDestroy,
} from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { Observable, forkJoin, of, Subject } from "rxjs";
import { debounceTime, first, map, switchMap, takeUntil } from "rxjs/operators";
import { CoberturasService } from "src/app/core/http/apiProducto/coberturas.service";
import { FraccionamientoService } from "src/app/core/http/apiProducto/fraccionamiento.service";
import { ObjetosService } from "src/app/core/http/apiProducto/objetos.service";
import { QuoteService } from "src/app/core/services/quote/quote.service";
import { SpinnerService } from "src/app/core/services/spinner/spinner.service";
import { setCoverages } from "src/app/state/coverages/coverages.actions";
import {
  selectObject,
  setObjects,
  setSelectedProductPrice,
} from "src/app/state/objects/objects.actions";
import { IObject } from "src/app/state/objects/objects.models";
import { selectSelectedObject } from "src/app/state/objects/objects.selectors";
import { setPaymentFractionState } from "src/app/state/paymentFractions/paymentFractions.actions";
import {
  resetQuote,
  setQuote,
} from "src/app/state/quotation/quotation.actions";
import { IQuotation } from "src/app/state/quotation/quotation.models";
import { selectPartner, selectUser } from "src/app/state/user/user.selectors";
import { AutocompleteService } from "../autocomplete/autocomplete.service";
import {
  DynamicFormConfig,
  FieldConfig,
} from "../dynamic-form/dynamic-form-config.interface";
import { DynamicFormComponent } from "../dynamic-form/dynamic-form.component";
import { JsonConfiguration } from "./json";
import {
  ISelectProductOption,
  ReadOnlyData,
} from "./interfaces/productPriceComponent.interface";
import { ICoverage } from "src/app/state/coverages/coverages.models";
import { IPaymentFraction } from "src/app/state/paymentFractions/paymentFractions.models";
import IHttpResponse from "src/app/core/interfaces/shared/IHttpResponse";
import { TranslateService } from "@ngx-translate/core";
import { DynamicFormService } from "../dynamic-form/dynamic-form.service";
import { PersonaService } from "src/app/core/http/apiPersona/persona.service";

export interface IStatus {
  showInfo?: boolean;
  text?: string;
  backgroundColorClass?: string;
}

@Component({
  selector: "app-product-price-form",
  templateUrl: "./product-price-form.component.html",
  styleUrls: ["./product-price-form.component.scss"],
  providers: [JsonConfiguration],
})
export class ProductPriceFormComponent implements OnInit, OnDestroy {
  @ViewChild(DynamicFormComponent) formView: DynamicFormComponent;

  @Input() isReadOnly: boolean;
  @Input() readOnlyData?: ReadOnlyData;
  @Input() status?: IStatus;

  partnerCode: string;
  dynamicForm: DynamicFormConfig;
  aliasEntidad: string;
  idEntidad: number;

  translateKey: string = "calculator";

  selectProduct: { options: ISelectProductOption[]; isMultiple: boolean } = {
    options: [],
    isMultiple: false,
  };

  productInput = new FormControl("", [Validators.required]);
  priceInput = new FormControl("", [Validators.required]);

  minValue: number = 0;
  maxValue: number = 0;

  pre_selectedObject: IObject;

  private destroy$ = new Subject<void>();
  private objectsCache: IObject[] = [];

  constructor(
    private jsonConfiguration: JsonConfiguration,
    private objetosService: ObjetosService,
    private autocompleteService: AutocompleteService,
    private store: Store,
    private quoteService: QuoteService,
    private coberturasService: CoberturasService,
    private cdRef: ChangeDetectorRef,
    private spinnerService: SpinnerService,
    private fraccionPagoService: FraccionamientoService,
    private translateService: TranslateService,
    private dynamicFormService: DynamicFormService,
    private personaService: PersonaService,
  ) { }

  ngOnInit(): void {
    this.getPartnerFromStorage();
    this.getObjectFromStorage();
    this.getPersonFromStorage();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getObjectFromStorage() {
    this.store
      .select(selectSelectedObject)
      .pipe(first())
      .subscribe(() => {
        this.getProducts();
      });
  }

  getPartnerFromStorage() {
    this.store
      .select(selectPartner)
      .pipe(first())
      .subscribe((res) => {
        if (res.codigoMediador) {
          this.partnerCode = res.codigoMediador;
          this.aliasEntidad = res.alias;
          this.idEntidad = res.idEntidad;
        }
      });
  }

  getPersonFromStorage() {
    this.store
      .select(selectUser)
      .pipe(first())
      .subscribe((res) => {
        this.personaService
          .getPersonAndPartnerByTokenOID(res.idTokenClaims.oid)
          .pipe(takeUntil(this.destroy$))
          .subscribe();
      });
  }

  typeOfProduct_valueChange_listener() {
    const typeOfProduct_formcontrol = this.formView.form.controls.typeOfProduct;

    typeOfProduct_formcontrol.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        const product = this.selectProduct.options.find(
          (option) => option.name === value,
        );
        this.price_set_validations(product);
        this.store.dispatch(selectObject({ object: product }));
        this.store.dispatch(resetQuote());
      });
  }

  typeOfProducto_set_initialValue() {
    const typeOfProduct_formcontrol = this.formView.form.controls.typeOfProduct;
    let product = this.selectProduct.options[0];
    if (this.pre_selectedObject) {
      product = this.selectProduct.options.find(
        (x) => x.id === this.pre_selectedObject.id,
      );
    }
    typeOfProduct_formcontrol.patchValue(product.name, {
      onlySelf: true,
      emitEvent: false,
    });
    this.price_set_validations(product);
    this.store.dispatch(selectObject({ object: product }));
  }

  price_set_validations(product: ISelectProductOption) {
    const minValueValidation =
      this.jsonConfiguration.generateMinValueValidation(product.priceMin);
    const maxValueValidation =
      this.jsonConfiguration.generateMaxValueValidation(product.priceMax);
    const price_formcontrol = this.formView.form.controls.price;
    const price_dynamicFormField = this.dynamicForm.fields.find(
      (x) => x.name === "price",
    );

    price_dynamicFormField.validations = [
      minValueValidation,
      maxValueValidation,
    ];
    price_dynamicFormField.value = "0 €";
    price_dynamicFormField.data = "0 €";

    price_formcontrol.setValidators([
      minValueValidation.validator,
      maxValueValidation.validator,
    ]);
    price_formcontrol.reset("0 €", { onlySelf: true, emitEvent: false });

    this.cdRef.detectChanges();
  }

  price_valueChange_listener() {
    const formcontrol_price = this.formView.form.controls.price;
    formcontrol_price.valueChanges
      .pipe(debounceTime(2000), takeUntil(this.destroy$))
      .subscribe(() => {
        this.store.dispatch(resetQuote());
        if (formcontrol_price.valid && formcontrol_price.value) this.cotizar();
      });
  }

  buyDate_valueChange_listener() {
    const formControlBuyPrice = this.formView.form.controls.buyDate;
    const formcontrol_price = this.formView.form.controls.price;

    formControlBuyPrice.valueChanges
      .pipe(debounceTime(2000), takeUntil(this.destroy$))
      .subscribe(() => {
        this.store.dispatch(resetQuote());
        if (
          formControlBuyPrice.valid &&
          formControlBuyPrice.value &&
          formcontrol_price.valid &&
          formcontrol_price.value
        ) {
          this.cotizar();
        }
      });
  }

  price_set_initialValue() {
    const price_formcontrol = this.formView.form.controls.price;
    const product_price =
      (this.pre_selectedObject &&
        this.pre_selectedObject.selectedProductPrice) ||
      0;
    if (product_price)
      this.store.dispatch(
        setSelectedProductPrice({ selectedProductPrice: product_price }),
      );
    price_formcontrol.patchValue(`${product_price} €`, {
      onlySelf: true,
      emitEvent: false,
    });
  }

  cotizar() {
    let price = this.obtenerPrecio();
    const buyDate = this.obtenerFechaCompra();
    price = this.reemplazarComasPorPuntos(price);
    const product = this.obtenerProductoSeleccionado();
    this.establecerPrecioProductoSeleccionado(price);
    const data = this.crearDatosCotizacion(price, product, buyDate);
    this.spinnerService.show();
    this.realizarCotizacion(data);
  }

  obtenerPrecio() {
    return this.formView.form.controls.price.value;
  }
  obtenerFechaCompra() {
    return this.formView.form.controls.buyDate.value;
  }
  reemplazarComasPorPuntos(price) {
    return price.indexOf(",") > -1 ? price.replace(/,/g, ".") : price;
  }
  obtenerProductoSeleccionado() {
    return this.selectProduct.options.find(
      (option) =>
        option.name === this.formView.form.controls.typeOfProduct.value,
    );
  }
  establecerPrecioProductoSeleccionado(price) {
    this.store.dispatch(
      setSelectedProductPrice({ selectedProductPrice: price }),
    );
  }
  crearDatosCotizacion(price, product, buyDate) {
    return {
      partnerCode: this.partnerCode,
      price,
      product,
      aliasEntidad: this.aliasEntidad,
      antiguedad: buyDate,
    };
  }

  realizarCotizacion(data) {
    this.quoteService
      .quote(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe((cotizacion) => {
        const quotation = {
          idCotizacion: cotizacion.idCotizacion,
          primaTotal: cotizacion.anual,
        };
        this.store.dispatch(setQuote({ quote: quotation }));
        const coverages$ = this.fetchCoveragesAsync(quotation);
        const paymentFrations$ = this.fetchPaymentFractionAsync(quotation);
        this.obtenerDatosCoberturasYFraccionesPago(
          coverages$,
          paymentFrations$,
        );
      });
  }

  obtenerDatosCoberturasYFraccionesPago(
    coverages$: Observable<ICoverage[]>,
    paymentFrations$: Observable<IHttpResponse<IPaymentFraction[]>>,
  ) {
    forkJoin({ coverage: coverages$, paymentFractions: paymentFrations$ })
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.store.dispatch(setCoverages({ coverages: response.coverage }));
        const paymentFractionsData = response.paymentFractions.data;
        const paymentFractionSelected =
          paymentFractionsData.length > 0 ? paymentFractionsData[0] : null;
        this.store.dispatch(
          setPaymentFractionState({
            new_state: {
              list: paymentFractionsData,
              selected: paymentFractionSelected,
            },
          }),
        );
        this.spinnerService.hide();
      });
  }

  fetchCoveragesAsync(cotizacion: IQuotation) {
    const coberturas_obligatorias$ = this.coberturasService
      .getCoberturasObligatoriasByIdCotizacion(cotizacion.idCotizacion)
      .pipe(first());
    const coberturas_opcionales$ = this.coberturasService
      .getCoberturasOpcionales(cotizacion.idCotizacion)
      .pipe(first());
    return forkJoin([coberturas_obligatorias$, coberturas_opcionales$]).pipe(
      map(([obligatorias, opcionales]) => {
        obligatorias.forEach((x) => (x.mandatory = true));
        opcionales.forEach((x) => (x.mandatory = false));
        return [...obligatorias, ...opcionales];
      }),
    );
  }

  fetchPaymentFractionAsync(cotizacion: IQuotation) {
    return this.fraccionPagoService
      .getFraccionamiento(cotizacion.idCotizacion)
      .pipe(first());
  }

  setDynamicForm(
    product_pre_loaded_config: FieldConfig = {},
    price_pre_loaded_config = {},
  ): void {
    const productField = this.generateProductField(product_pre_loaded_config);
    const priceField = this.jsonConfiguration.generatePriceField(
      price_pre_loaded_config,
    );
    const buyDate = this.jsonConfiguration.generate_fecha_compra_field();
    this.dynamicForm = {
      fxLayout: "row wrap",
      fxLayoutGap: "2rem",
      fxLayoutAlign: "start start",
      fields: [productField, priceField, buyDate],
    };
    this.cdRef.detectChanges();
    this.spinnerService.hide();
  }


  getProducts() {

    if (this.readOnlyData || this.objectsCache.length > 0) {
      this.actualizarOpcionesDeProducto(this.objectsCache);
      this.autocompleteService.ondataListChange(this.selectProduct.options);

      const pricePreLoadedConfig = { info: { content: [] } };
      this.setDynamicForm(null, pricePreLoadedConfig);

      this.accionesInicialesSiNoEsSoloLectura();
      setTimeout(() => console.timeEnd("getProducts"), 0);
      return;
    }

    this.spinnerService.show();

    this.objetosService.getObjects(this.idEntidad).pipe(
      first(),
      switchMap(resp => {
        if (resp.succeeded) {
          this.objectsCache = resp.data;

          this.store.dispatch(setObjects({ objects: resp.data }));
          this.actualizarOpcionesDeProducto(resp.data);
          this.autocompleteService.ondataListChange(this.selectProduct.options);

          return this.objetosService.getCoverageInfoTextByPantallaInfo("ValorObjeto").pipe(
            first(),
            map(res => res.data.sort((a, b) => a.nivel - b.nivel || a.orden - b.orden))
          );
        } else {
          return of([]);
        }
      }),
      takeUntil(this.destroy$)
    ).subscribe(details => {
      const pricePreLoadedConfig = { info: { content: details } };
      this.setDynamicForm(null, pricePreLoadedConfig);

      this.accionesInicialesSiNoEsSoloLectura();

      this.spinnerService.hide();
    });
  }

  generateProductField(pre_loaded_config: FieldConfig): FieldConfig {
    const inputName = "typeOfProduct";
    const label = this.translateService.instant(
      `${this.translateKey}.input.${inputName}.label`,
    );
    const productValidation =
      this.dynamicFormService.generateRequiredValidation(this.translateKey);
    return this.dynamicFormService.generateField(
      inputName,
      label,
      "autocomplete",
      null,
      null,
      "100%",
      [productValidation],
      { select: this.selectProduct, ...pre_loaded_config },
    );
  }

  actualizarOpcionesDeProducto(objects) {
    this.selectProduct.options = objects.map((element) => ({
      ...element,
      code: element.id,
      name: element.descripcion,
      icon: element.icono,
      priceMin: element.valorMin,
      priceMax: element.valorMax,
    }));
  }

  accionesInicialesSiNoEsSoloLectura() {
    if (!this.isReadOnly) {
      this.typeOfProduct_valueChange_listener();
      this.price_valueChange_listener();
      this.typeOfProducto_set_initialValue();
      this.price_set_initialValue();
      this.buyDate_valueChange_listener();
    }
  }
}
